import { COLOR_TO_TYPE } from './useValidationUtils'

/**
 * Returns a promise that resolves after a specified delay
 * @param {number} [ms=400] - The delay in milliseconds
 * @returns {Promise<void>}
 */
const delay = (ms = 400) => {
    return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Throttles a function using requestAnimationFrame
 * @param {Function} fn - The function to throttle
 * @returns {Function} The throttled function
 */
const throttle = (fn) => {
    let isThrottled = false
    return function (...args) {
        if (isThrottled) return
        isThrottled = true
        requestAnimationFrame(() => {
            fn.apply(this, args)
            isThrottled = false
        })
    }
}

/**
 * Checks if two arrays are equal (order doesn't matter)
 * @param {Array} a - The first array
 * @param {Array} b - The second array
 * @returns {boolean} True if arrays have the same elements
 */
const arraysAreEqual = (a, b) => {
    if (a.length !== b.length) return false
    const aSorted = [...a].sort()
    const bSorted = [...b].sort()
    return aSorted.every((v, i) => v === bSorted[i])
}

/**
 * Checks if two arrays are equal and in the same order
 * @param {Array} a - The first array
 * @param {Array} b - The second array
 * @returns {boolean} True if arrays are equal and ordered the same
 */
const arraysAreEqualOrder = (a, b) => {
    if (a === b) return true
    if (!a || !b) return false
    if (a.length !== b.length) return false
    return a.every((val, i) => val === b[i])
}

const getPageTitle = (route) => {
    return route?.meta?.title || document.title.replace('- AppCheck', '').trim()
}

/**
 * Plugin for managing feature flags.
 * Provides global access to feature flag state (level and message) across Vue components.
 * @type {Object}
 * @property {Function} install - Vue plugin install method
 */
const featureFlagPlugin = {
    /**
     * Install method for Vue plugin
     * @param {Vue} Vue - The Vue instance
     */
    install(Vue) {
        const state = Vue.observable({
            featureFlag: { level: '', message: '' },
        })

        /**
         * Sets the feature flag state
         * @param {Object} params - Feature flag options
         * @param {string} [params.level=''] - Feature flag level, e.g. 'alpha' or 'beta'
         * @param {string} [params.message=''] - Feature flag message/description
         */
        Vue.prototype.$setFeatureFlag = ({ level = '', message = '' } = {}) => {
            state.featureFlag.level = level
            state.featureFlag.message = message
        }

        /**
         * Reactive getter for feature flag state
         * @name $featureFlag
         * @memberof Vue.prototype
         * @type {Object}
         * @property {string} level - Feature flag level
         * @property {string} message - Feature flag message
         */
        Object.defineProperty(Vue.prototype, '$featureFlag', {
            get() {
                return state.featureFlag
            },
        })
    },
}

const IMPACT_MAPPINGS = {
    critical: {
        level: '5',
        label: 'Critical',
        icon: 'fa-triangle-exclamation',
        color: 'red',
        iconType: COLOR_TO_TYPE['red'],
    },
    high: {
        level: '4',
        label: 'High',
        icon: 'fa-chevrons-up',
        color: 'orange',

        iconType: COLOR_TO_TYPE['orange'],
    },
    medium: {
        level: '3',
        label: 'Medium',
        icon: 'fa-bug',
        color: 'yellow',

        iconType: COLOR_TO_TYPE['yellow'],
    },
    low: {
        level: '2',
        label: 'Low',
        icon: 'fa-chevrons-down',
        color: 'green',
        iconType: COLOR_TO_TYPE['green'],
    },
    info: {
        level: '1',
        label: 'Info',
        icon: 'fa-circle-info',
        color: 'blue',
        iconType: COLOR_TO_TYPE['blue'],
    },
    investigate: {
        level: '0',
        label: 'Investigate',
        icon: 'fa-binoculars',
        color: 'light-slate',
        iconType: COLOR_TO_TYPE['light-slate'],
    },
    unknown: {
        label: 'Unknown',
        icon: 'fa-question-circle',
        color: 'light-slate',
        iconType: COLOR_TO_TYPE['light-slate'],
    },
}

const getCssVar = (variableName) => {
    return getComputedStyle(document.documentElement)
        .getPropertyValue(variableName)
        .trim()
}

export {
    delay,
    throttle,
    arraysAreEqual,
    arraysAreEqualOrder,
    getPageTitle,
    getCssVar,
    featureFlagPlugin,
    IMPACT_MAPPINGS,
}
